// Neutral monsters (Бегемот, Грифон): one per den at LAYOUT.bosses, passive until hit, and a timed team
// buff for whichever team lands the killing blow. Same shape as artifacts.ts — a data table plus functions
// over (Game, …) — so game.ts only wires hooks in and never carries monster rules itself.
import * as THREE from 'three';
import { LAYOUT, type Team } from './layout.js';
import type { Point } from './nav.js';
import { maxHpOf, type DamageMark } from './artifacts.js';
import type { Game, Hero } from './game.js';
import type { Summon } from './summons.js';

export type MonsterKind = 'griffin' | 'behemoth';
export type MonsterId = number;  // index into LAYOUT.bosses (and Game.monsters)
export type MonsterState = 'idle' | 'wander' | 'chase' | 'windup' | 'recover' | 'return';

// windup matches the Attack clips' impact frame (0.958 s); the 1.5 s cycle covers the 1.417 s clip.
const BASE = {
  hp: 450, dmg: 30, attackCd: 1.5, windup: 0.95, range: 2.4, reachTolerance: 0.5, radius: 1.3, speed: 4.4, turn: 6,
  wander: 3, wanderEvery: [2, 4] as const, leash: 8, homeHeal: 1 / 3,  // share of lost hp restored on reaching the den
};
export const MONSTERS = {
  behemoth: { ...BASE, buff: { dur: 180, hpPct: 0.6, moveSpeed: 0, atkSpeed: 0 } },
  griffin: { ...BASE, buff: { dur: 180, hpPct: 0, moveSpeed: 0.3, atkSpeed: 0.3 } },
} as const satisfies Record<MonsterKind, unknown>;

export interface Monster {
  id: MonsterId;
  kind: MonsterKind;
  den: THREE.Vector3;
  pos: THREE.Vector3;
  yaw: number;
  wantYaw: number;
  hp: number;
  alive: boolean;
  respawnAt: number;
  state: MonsterState;
  stateT: number;
  target: Hero | null;
  swing: Hero | null;  // locked at windup start: a retarget mid-windup never redirects the blow already coming
  path: Point[];
  repathAt: number;
  nextWanderAt: number;
  attackReadyAt: number;
  marks: DamageMark[];  // Клеймо, same as a hero's; gone on death and on a leash reset
}

/* Anything a hero's attack or skill can be aimed at: a hero, a neutral monster or a summoned creature (summons.ts). */
export type Target = Hero | Monster | Summon;
export const isMonster = (t: Target): t is Monster => 'den' in t;
export const isSummon = (t: Target): t is Summon => 'owner' in t;
export const isHero = (t: Target): t is Hero => !('den' in t) && !('owner' in t);
/* How much further than a hero's own body a hit on this target reaches — 0 for a hero. */
export const bulkOf = (t: Target) => (isMonster(t) ? MONSTERS[t.kind].radius : isSummon(t) ? t.radius : 0);

/* Sim-time a team's buff runs until, or null. Derived onto each hero as `Hero.boon` by applyBoon(). */
export type TeamBuffs = Record<Team, Record<MonsterKind, number | null>>;
export type Boon = { hpPct: number; moveSpeed: number; atkSpeed: number };
export const NO_BOON: Readonly<Boon> = { hpPct: 0, moveSpeed: 0, atkSpeed: 0 };
export const noTeamBuffs = (): TeamBuffs => ({ a: { behemoth: null, griffin: null }, b: { behemoth: null, griffin: null } });

const flatDist = (a: { x: number; z: number }, b: { x: number; z: number }) => Math.hypot(a.x - b.x, a.z - b.z);
const angDiff = (a: number, b: number) => Math.atan2(Math.sin(b - a), Math.cos(b - a));
const MONSTER_KINDS: MonsterKind[] = ['behemoth', 'griffin'];

function freshMonster(g: Game, id: MonsterId): Monster {
  const { kind, pos: [x, z] } = LAYOUT.bosses[id]!;
  const m: Monster = {
    id, kind, den: new THREE.Vector3(x, 0, z), pos: new THREE.Vector3(x, 0, z), yaw: 0, wantYaw: 0,
    hp: MONSTERS[kind].hp, alive: true, respawnAt: 0,
    state: 'idle', stateT: 0, target: null, swing: null, path: [], repathAt: 0, nextWanderAt: 0, attackReadyAt: 0, marks: [],
  };
  scheduleWander(g, m);
  return m;
}

export function spawnMonsters(g: Game): Monster[] {
  return LAYOUT.bosses.map((_, id) => freshMonster(g, id));
}

function setState(m: Monster, s: MonsterState) { m.state = s; m.stateT = 0; }
function scheduleWander(g: Game, m: Monster) {
  const [lo, hi] = MONSTERS[m.kind].wanderEvery;
  m.nextWanderAt = g.t + lo + g.monsterRng() * (hi - lo);
}

function moveAlong(g: Game, m: Monster, dt: number, leashed = false) {
  let step = MONSTERS[m.kind].speed * g.groundSpeed(m.pos.x, m.pos.z) * dt;
  while (step > 0 && m.path.length) {
    const wp = m.path[0] as Point, dx = wp.x - m.pos.x, dz = wp.z - m.pos.z, d = Math.hypot(dx, dz);
    if (d > 1e-4) m.wantYaw = Math.atan2(dx, dz);
    if (d <= step) { m.pos.set(wp.x, 0, wp.z); m.path.shift(); step -= d; }
    else { m.pos.x += dx / d * step; m.pos.z += dz / d * step; step = 0; }
  }
  if (leashed) clampToLeash(g, m);
}

/* A chasing monster never leaves the leash circle around its den. Only the position is projected back —
   the path is kept, so it slides along the edge after a target circling just outside instead of stalling
   until the next repath. */
function clampToLeash(g: Game, m: Monster) {
  const leash = MONSTERS[m.kind].leash, d = flatDist(m.pos, m.den);
  if (d <= leash) return;
  const ux = (m.pos.x - m.den.x) / d, uz = (m.pos.z - m.den.z) / d;
  // Rare: the circle itself crosses cover — settle a little inside it instead.
  const r = g.nav.isWalkable(m.den.x + ux * leash, m.den.z + uz * leash) ? leash : leash - 0.5;
  m.pos.set(m.den.x + ux * r, 0, m.den.z + uz * r);
}

/* Kept while alive, uncloaked and still reachable from somewhere inside the leash circle — a ranged hero
   shooting from the edge of its range must not reset the monster just by standing there. */
function engaged(g: Game, m: Monster): boolean {
  const tg = m.target, def = MONSTERS[m.kind];
  return !!tg && tg.alive && g.t >= tg.cloakUntil && !g.silhouette(tg) && flatDist(tg.pos, m.den) <= def.leash + def.range + def.reachTolerance;
}

function startReturn(g: Game, m: Monster) {
  m.target = null;
  m.marks = [];
  m.path = g.nav.findPath(m.pos.x, m.pos.z, m.den.x, m.den.z);
  setState(m, 'return');
}

export function updateMonster(g: Game, m: Monster, dt: number) {
  if (!m.alive) return;
  const def = MONSTERS[m.kind];
  m.stateT += dt;
  if (m.marks.length) m.marks = m.marks.filter(k => k.until > g.t);
  if (g.monsterAi) {
    switch (m.state) {
      case 'idle':
        if (g.t >= m.nextWanderAt) {
          const a = g.monsterRng() * Math.PI * 2, r = g.monsterRng() * def.wander;
          const x = m.den.x + Math.sin(a) * r, z = m.den.z + Math.cos(a) * r;
          if (g.nav.isWalkable(x, z)) { m.path = g.nav.findPath(m.pos.x, m.pos.z, x, z); setState(m, 'wander'); }
          scheduleWander(g, m);
        }
        break;
      case 'wander':
        moveAlong(g, m, dt);
        if (!m.path.length) setState(m, 'idle');
        break;
      case 'windup': {
        // Finishes against the hero it was aimed at, whoever has hit the monster since.
        if (m.stateT >= def.windup) {
          const sw = m.swing;
          m.swing = null;
          // Re-checked against where the target stands NOW: stepping out during the windup dodges it.
          if (sw && sw.alive && g.t >= sw.cloakUntil && !g.silhouette(sw) && flatDist(m.pos, sw.pos) <= def.range + def.reachTolerance) {
            g.damage(sw, def.dmg, null, { fromPos: m.pos, by: m });
          }
          setState(m, 'recover');
        }
        break;
      }
      case 'chase': case 'recover': {
        if (!engaged(g, m)) { startReturn(g, m); break; }
        const tg = m.target as Hero, d = flatDist(m.pos, tg.pos);
        // Pinned at the leash edge it cannot close in, so it swings at the full reach engaged() allows.
        const pinned = flatDist(m.pos, m.den) >= def.leash - 0.5 - 1e-6;
        const reach = pinned ? def.range + def.reachTolerance : def.range;
        if (m.state === 'recover') {
          if (g.t >= m.attackReadyAt) setState(m, 'chase');
        } else if (d > reach) {
          if (g.t >= m.repathAt) { m.path = g.nav.findPath(m.pos.x, m.pos.z, tg.pos.x, tg.pos.z); m.repathAt = g.t + 0.3; }
          moveAlong(g, m, dt, true);
        } else {
          m.path = [];
          m.wantYaw = Math.atan2(tg.pos.x - m.pos.x, tg.pos.z - m.pos.z);
          if (g.t >= m.attackReadyAt) {
            m.attackReadyAt = g.t + def.attackCd;
            m.swing = tg;
            setState(m, 'windup');
          }
        }
        break;
      }
      case 'return':
        moveAlong(g, m, dt);
        if (!m.path.length) {
          m.hp += (def.hp - m.hp) * def.homeHeal;
          setState(m, 'idle');
          scheduleWander(g, m);
        }
        break;
    }
  }
  const turn = angDiff(m.yaw, m.wantYaw);
  m.yaw += Math.sign(turn) * Math.min(Math.abs(turn), def.turn * dt);
}

/* Entered from Game.damage() after the attacker's own damage buffs are applied. CC, knockback and every
   hero-only modifier never reach here — a monster only ever loses hp and picks its attacker as target. */
export function damageMonster(g: Game, m: Monster, amount: number, src: Hero | null, skill: boolean, by: Target | null): number {
  if (!g.monsterAi || !m.alive || m.state === 'return') return 0;
  m.hp -= amount;
  if (skill && amount > 0) g.emit({ type: 'monsterNum', monster: m.id, value: Math.round(amount) });
  g.emitHit({ type: 'fx', name: 'hit', pos: g.pos2(m.pos), color: src && src.def.bolt ? src.def.bolt : (src && src.team === 'a' ? 0xffb347 : 0x7fe0ff), big: skill }, undefined, by);
  if (src) {
    m.target = src;
    if (m.state === 'idle' || m.state === 'wander') { m.path = []; m.repathAt = 0; setState(m, 'chase'); }
  }
  if (m.hp <= 0) killMonster(g, m, src);
  return amount;
}

/* The killer's team gets the buff even when the killer is already dead (a DoT finishing the job). The
   den stays empty for exactly the buff's duration: updateTeamBuffs() respawns it as the buff runs out. */
export function killMonster(g: Game, m: Monster, killer: Hero | null) {
  const def = MONSTERS[m.kind];
  m.alive = false;
  m.hp = 0;
  m.target = null;
  m.swing = null;
  m.path = [];
  m.marks = [];
  setState(m, 'idle');
  m.respawnAt = g.t + def.buff.dur;
  g.emit({ type: 'fx', name: 'monsterSlain', pos: g.pos2(m.pos), team: killer ? killer.team : null });
  if (killer) { grantTeamBuff(g, killer.team, m.kind); g.stats.monsterKilled(killer.id); }
}

export function grantTeamBuff(g: Game, team: Team, kind: MonsterKind) {
  g.teamBuffs[team][kind] = g.t + MONSTERS[kind].buff.dur;
  applyBoon(g, team);
}

export function updateTeamBuffs(g: Game) {
  for (const team of ['a', 'b'] as Team[]) {
    let changed = false;
    for (const kind of MONSTER_KINDS) {
      const until = g.teamBuffs[team][kind];
      if (until !== null && g.t >= until) { g.teamBuffs[team][kind] = null; changed = true; }
    }
    if (changed) applyBoon(g, team);
  }
  for (const m of g.monsters) if (!m.alive && g.t >= m.respawnAt) respawnMonster(g, m);
}

/* In place, so any reference held to a monster (the view, an order) stays valid across its respawns. */
export function respawnMonster(g: Game, m: Monster) {
  Object.assign(m, freshMonster(g, m.id));
}

/* Recomputes every hero's `boon` on this team from teamBuffs. Gaining max hp raises current hp by the same
   amount; losing it clamps down to the new cap, like selling Наплечник (artifacts.ts sellArtifact). A dead
   hero keeps hp 0 — respawn() reads maxHpOf() and so picks the boon up on its own. */
export function applyBoon(g: Game, team: Team) {
  const tb = g.teamBuffs[team];
  const boon: Boon = { ...NO_BOON };
  for (const kind of MONSTER_KINDS) {
    if (tb[kind] === null) continue;
    const b = MONSTERS[kind].buff;
    boon.hpPct += b.hpPct; boon.moveSpeed += b.moveSpeed; boon.atkSpeed += b.atkSpeed;
  }
  for (const h of g.heroes) {
    if (h.team !== team) continue;
    const before = maxHpOf(h);
    h.boon = { ...boon };
    const after = maxHpOf(h);
    if (!h.alive) continue;
    if (after > before) h.hp += after - before;
    if (h.hp > after) h.hp = after;
  }
}

/* For a client mirroring the server: `remaining` is seconds left per buff (null = none), not sim time. */
export function setTeamBuffs(g: Game, remaining: TeamBuffs) {
  for (const team of ['a', 'b'] as Team[]) {
    for (const kind of MONSTER_KINDS) {
      const r = remaining[team][kind];
      g.teamBuffs[team][kind] = r === null ? null : g.t + r;
    }
    applyBoon(g, team);
  }
}

/* Monsters have no stealth: ordinary fog and tree line-of-sight apply, like a totem. */
export function monsterVisibleTo(g: Game, team: Team, m: Monster): boolean {
  return g.inSight(team, m.pos);
}
